import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { classnames } from 'tailwindcss-classnames';
import useScreenWidth from 'lib/util/useScreenWidth';
import ArrowCollapse from 'components/helpers/SvgIcon/icons/arrow-collape';
import ArroweExpand from 'components/helpers/SvgIcon/icons/arrow-expand';
import { useState } from 'react';
import { Field, LinkField, Link as SitecoreLink } from '@sitecore-jss/sitecore-jss-nextjs';
import SvgIcon from 'components/helpers/SvgIcon/SvgIcon';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import IconClose from 'components/helpers/SvgIcon/icons/icon-close';
import { pageNavigationDataLayerPush, menuLocation } from 'lib/util/gtm-datalayer/';

export type QuickLinksListProps = Sitecore.Override.ComponentBase &
  Components.Navigation.QuickLinkList.Fields.QuickLinksItemList & {
    fields: {};
  };

export type LinksDataProps = {
  id: string;
  displayName: string;
  name: string;
  fields: {
    links: LinkField;
  };
};

export type CategoryProps = {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    linksDescription: Field<string>;
    linksTitle: Field<string>;
    linksItem: LinksDataProps[];
  };
};

const QuickLinksList = ({ fields }: QuickLinksListProps) => {
  const cureScreenWidth = useScreenWidth();
  const categories = fields?.categoryLinks as CategoryProps[];
  const [isOpen, setIsOpen] = useState(categories.map(() => false)); // Initialize isOpen state for each category
  const [visibleItem, setVisibleItem] = useState<string | null>(null);

  const toggleCategory = (index: number, itemName: string) => {
    // Toggle the state for the specified category
    setIsOpen((prevIsOpen) => {
      const newState = [...prevIsOpen];
      newState[index] = !newState[index];
      return newState;
    });
    pageNavigationDataLayerPush(itemName, menuLocation[7]);
  };

  // Fail out if we don't have any fields
  if (!fields) {
    return <></>;
  }

  const mobileLayout = () => {
    return (
      <>
        <ul>
          {categories.map((item, index) => (
            <li
              key={item.id}
              className={classnames(`border-t`, `border-solid`, `border-gray-midGray`, `py-[24px]`)}
            >
              <div>
                <button
                  type="button"
                  className={classnames(`font-semibold`, `flex`, `text-left`)}
                  onClick={() => toggleCategory(index, item.displayName)} // Toggle the category when the button is clicked
                >
                  <span className={classnames(`self-center`, `pr-2.5`)}>
                    {isOpen[index] ? (
                      <ArroweExpand className={classnames(`text-blue-morning`)} />
                    ) : (
                      <ArrowCollapse className={classnames(`text-blue-morning`)} />
                    )}
                  </span>
                  <div>
                    <h3 className={classnames(`text-lg`, `font-medium`, `leading-7`)}>
                      {item?.displayName}
                    </h3>
                    <p className={classnames(`text-base`, `font-regular`, `leading-4`)}>
                      {
                        (
                          item as unknown as Components.Navigation.QuickLinkList.Fields.QuickLinksItem
                        ).fields?.linksDescription?.value
                      }
                    </p>
                  </div>
                </button>
              </div>
              <div className={classnames(`pl-[30px]`, `pr-[20px]`)}>
                <div
                  className={classnames(`rte`, {
                    ['my-[30px]']: isOpen[index],
                    ['p-0']: !isOpen[index],
                    ['hidden']: !isOpen[index],
                  })}
                >
                  {item?.fields?.linksItem.map((subItem) => (
                    <div
                      key={subItem.id}
                      className={classnames(
                        `quicklist-shadow`,
                        `mb-[16px]`,
                        `flex`,
                        `items-center`,
                        `justify-center`,
                        `rounded-[15px]`,
                        `border`,
                        `border-solid`,
                        `border-gray-whisper`,
                        `bg-white`,
                        `px-[8px]`,
                        `py-[12px]`,
                        `text-center`,
                        `text-body`,
                        `font-medium`,
                        `leading-2`,
                        `text-blue`
                      )}
                    >
                      <SitecoreLink
                        field={subItem?.fields?.links}
                        className={classnames(`character-limit`, `!no-underline`)}
                      >
                        {subItem?.fields?.links?.value?.text}
                      </SitecoreLink>
                    </div>
                  ))}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </>
    );
  };
  const desktopLayout = () => {
    const toggleItemVisibility = (id: string | null, itemName: string) => {
      setVisibleItem((prevItem: string | null) => (prevItem === id ? null : id));
      pageNavigationDataLayerPush(itemName, menuLocation[7]);
    };

    return (
      <>
        <div className="relative flex">
          <div className="w-6/12">
            <ul className={classnames(`quicklinks-list`)}>
              {categories.map((item) => (
                <div className="" key={item.id}>
                  <li
                    className={classnames(
                      `border-t`,
                      `border-solid`,
                      `border-gray-midGray`,
                      `py-[17px]`,
                      `pl-[14px]`,
                      `pr-[9px]`,
                      {
                        ['bg-blue-navy']: item.id === visibleItem,
                        ['text-white']: item.id === visibleItem,
                      }
                    )}
                  >
                    <button
                      type="button"
                      className={classnames(`flex`, `w-full`, `text-left`, `font-semibold`)}
                      onClick={() => toggleItemVisibility(item?.id, item?.displayName)}
                    >
                      <div className={classnames(`w-[95%]`)}>
                        <h3
                          className={classnames(
                            `mb-[3px]`,
                            `text-2xl`,
                            `font-medium`,
                            `leading-9`,
                            item.id === visibleItem ? `text-white` : `text-black`
                          )}
                        >
                          {item?.displayName}
                        </h3>
                        <p className={classnames(`text-base`, `font-regular`, `leading-4`)}>
                          {
                            (
                              item as unknown as Components.Navigation.QuickLinkList.Fields.QuickLinksItem
                            ).fields?.linksDescription?.value
                          }
                        </p>
                      </div>
                      {item.id === visibleItem ? (
                        <span className={classnames(`self-center`)}>
                          <IconClose className={classnames(`text-white`)} />
                        </span>
                      ) : (
                        <span className={classnames(`arrow-animation`, `self-center`)}>
                          <SvgIcon
                            className={classnames(`mr-2`, `text-blue`)}
                            icon="arrow-right"
                            size="sm"
                            viewBoxSize="0 0 48 48"
                          />
                        </span>
                      )}
                    </button>
                  </li>
                  <ul
                    className={classnames(
                      `absolute`,
                      `top-0`,
                      `left-[52.5%]`,
                      `w-[47.5%]`,
                      visibleItem === item.id
                        ? classnames(`flex`, `flex-wrap`, `gap-x-[30px]`)
                        : `hidden`
                    )}
                  >
                    {item?.fields?.linksItem?.map((data: LinksDataProps) => (
                      <li
                        key={data.id}
                        className={classnames(
                          `quicklist-shadow`,
                          `min-h-[66px]`,
                          `w-[46%]`,
                          `mb-[16px]`,
                          `flex`,
                          `items-center`,
                          `justify-center`,
                          `rounded-[15px]`,
                          `border`,
                          `border-solid`,
                          `border-gray-whisper`,
                          `bg-white`,
                          `px-[8px]`,
                          `py-[9px]`,
                          `text-center`,
                          `text-body`,
                          `font-medium`,
                          `leading-2`,
                          `text-blue`,
                          `transition`,
                          `ease-in-out`,
                          `duration-300`,
                          `hover:bg-blue`,
                          `hover:text-white`
                        )}
                      >
                        <SitecoreLink
                          field={data?.fields?.links}
                          className={classnames(`character-limit`, `!no-underline`)}
                          onClick={() => {
                            const getDisplayText = data?.fields?.links?.value?.text;
                            const displayText = getDisplayText ? getDisplayText : 'undefined';
                            pageNavigationDataLayerPush(displayText, menuLocation[7]);
                          }}
                        >
                          {data?.fields?.links?.value?.text}
                        </SitecoreLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </ul>
          </div>
          <div className={classnames(`w-6/12`, `pl-[32px]`)}>
            {visibleItem === null ? (
              <ImageWrapper
                className={classnames(
                  'z-10',
                  'min-h-[250px]',
                  'min-w-[250px]',
                  'md:h-full',
                  'object-cover',
                  'rounded-tl-[15rem]',
                  'rounded-bl-[15rem]'
                )}
                field={fields?.categoryImage}
                layout="responsive"
              />
            ) : (
              ''
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <div data-component="authorable/navigation/quicklinkslist">
        <div className={classnames(`mt-[25px]`, `lg:min-h-[430px]`)}>
          {cureScreenWidth <= 990 ? mobileLayout() : desktopLayout()}
        </div>
      </div>
    </>
  );
};

export default QuickLinksList;
